import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetCampaignQuery,
  useGetCampaignContactsQuery,
  useGetAiCampaignLiveQuery,
  useStartAiCampaignMutation,
  usePauseAiCampaignMutation,
  useStopAiCampaignMutation,
} from "@/store/rest_api";
import { useAiCampaignSocket } from "@/hooks/useAiCampaignSocket";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import TelemarketingComplianceDialog from "@/components/pages/power-dialer/TelemarketingComplianceDialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Play,
  Pause,
  Square,
  Bot,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  Phone,
  PhoneOff,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import type CampaignContact from "@/model/interface/CampaignContact";

// ─── Sentiment badge ──────────────────────────────────────────────────────────

const SENTIMENT_CONFIG: Record<string, { label: string; icon: React.ElementType; pill: string }> = {
  INTERESTED:     { label: "Interested",     icon: TrendingUp,   pill: "status-pill-success" },
  NEUTRAL:        { label: "Neutral",        icon: Minus,        pill: "status-pill-neutral" },
  NOT_INTERESTED: { label: "Not interested", icon: TrendingDown, pill: "status-pill-destructive" },
};

function SentimentBadge({ sentiment }: { sentiment: string }) {
  const c = SENTIMENT_CONFIG[sentiment];
  if (!c) return null;
  const Icon = c.icon;
  return (
    <span className={c.pill}>
      <Icon className="h-3 w-3" />
      {c.label}
    </span>
  );
}

// ─── Contact status ───────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; icon: React.ElementType; pill: string }> = {
  QUEUED:     { label: "Queued",     icon: Clock,        pill: "status-pill-neutral" },
  DIALING:    { label: "Dialing",    icon: Phone,        pill: "status-pill-info" },
  RINGING:    { label: "Ringing",    icon: Phone,        pill: "status-pill-info" },
  ANSWERED:   { label: "Answered",   icon: Phone,        pill: "status-pill-warning" },
  CONNECTED:  { label: "Connected",  icon: Phone,        pill: "status-pill-warning" },
  COMPLETED:  { label: "Completed",  icon: CheckCircle2, pill: "status-pill-success" },
  NO_ANSWER:  { label: "No answer",  icon: PhoneOff,     pill: "status-pill-neutral" },
  BUSY:       { label: "Busy",       icon: PhoneOff,     pill: "status-pill-neutral" },
  FAILED:     { label: "Failed",     icon: XCircle,      pill: "status-pill-destructive" },
  SKIPPED:    { label: "Skipped",    icon: XCircle,      pill: "status-pill-neutral" },
};

function StatusBadge({ status }: { status: string }) {
  const c = STATUS_CONFIG[status] ?? { label: status, icon: Minus, pill: "status-pill-neutral" };
  const Icon = c.icon;
  return (
    <span className={c.pill}>
      <Icon className="h-3 w-3" />
      {c.label}
    </span>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, className }: { label: string; value: number; className?: string }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <div className={`text-2xl font-bold ${className ?? ""}`}>{value}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
      </CardContent>
    </Card>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AiCampaignLivePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: campaign, isLoading: campaignLoading, refetch: refetchCampaign } = useGetCampaignQuery(id!, { skip: !id });
  const isActive = campaign?.status === "ACTIVE";
  const { data: liveData, isLoading: liveLoading } = useGetAiCampaignLiveQuery(id!, {
    skip: !id,
    pollingInterval: isActive ? 15000 : 0,
  });
  // Use the standard contacts endpoint — it has proper @JsonView serialisation.
  // The /live endpoint is used only for computed stats.
  const { data: contactsData, isLoading: contactsLoading } = useGetCampaignContactsQuery(id!, {
    skip: !id,
    pollingInterval: isActive ? 15000 : 0,
  });

  const [startCampaign, { isLoading: isStarting }] = useStartAiCampaignMutation();
  const [pauseCampaign, { isLoading: isPausing }] = usePauseAiCampaignMutation();
  const [stopCampaign,  { isLoading: isStopping }]  = useStopAiCampaignMutation();
  const [isComplianceOpen, setIsComplianceOpen] = useState(false);

  const { events: socketEvents } = useAiCampaignSocket(id ?? null);

  // Merge server contacts with real-time socket updates
  const mergedContacts = useMemo<CampaignContact[]>(() => {
    const base: CampaignContact[] = contactsData ?? [];
    if (Object.keys(socketEvents).length === 0) return base;
    return base.map((c) => {
      const live = socketEvents[c.id];
      if (!live) return c;
      return {
        ...c,
        status: live.status as CampaignContact["status"],
        retryCount: live.retryCount,
        aiAgentCallId: live.aiAgentCallId ?? c.aiAgentCallId,
      };
    });
  }, [contactsData, socketEvents]);

  // Live-patched stats (fall back to server stats when no socket events)
  const stats = useMemo(() => {
    if (liveData?.stats && Object.keys(socketEvents).length === 0) return liveData.stats;
    return {
      total:     mergedContacts.length,
      queued:    mergedContacts.filter((c) => c.status === "QUEUED").length,
      active:    mergedContacts.filter((c) => ["DIALING","RINGING","ANSWERED","CONNECTED"].includes(c.status)).length,
      completed: mergedContacts.filter((c) => c.status === "COMPLETED").length,
      failed:    mergedContacts.filter((c) => ["FAILED","NO_ANSWER","BUSY"].includes(c.status)).length,
      skipped:   mergedContacts.filter((c) => c.status === "SKIPPED").length,
    };
  }, [mergedContacts, liveData, socketEvents]);

  const activeContacts   = mergedContacts.filter((c) => ["DIALING","RINGING","ANSWERED","CONNECTED"].includes(c.status));
  const completedContacts = mergedContacts.filter((c) => ["COMPLETED","FAILED","NO_ANSWER","BUSY","SKIPPED"].includes(c.status));

  const handleStart = () => {
    setIsComplianceOpen(true);
  };

  const handleStartConfirmed = async () => {
    setIsComplianceOpen(false);
    try {
      await startCampaign({ id: id!, acknowledgedRegistration: true }).unwrap();
      toast.success("Campaign started");
      refetchCampaign();
    } catch (e: any) {
      toast.error(e?.detail ?? "Failed to start campaign");
    }
  };

  const handlePause = async () => {
    try {
      await pauseCampaign(id!).unwrap();
      toast.success("Campaign paused");
      refetchCampaign();
    } catch (e: any) {
      toast.error(e?.detail ?? "Failed to pause campaign");
    }
  };

  const handleStop = async () => {
    try {
      await stopCampaign(id!).unwrap();
      toast.success("Campaign stopped");
      refetchCampaign();
    } catch (e: any) {
      toast.error(e?.detail ?? "Failed to stop campaign");
    }
  };

  const contactName = (c: CampaignContact) => {
    const first = c.contact?.firstName;
    const last  = c.contact?.lastName;
    const name  = [first, last].filter(Boolean).join(" ");
    return name || c.phoneNumber || "—";
  };

  const socketEntries = Object.values(socketEvents);

  if (campaignLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-sm text-destructive">Campaign not found</p>
        <Button variant="ghost" size="sm" onClick={() => navigate("/campaigns")}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
      </div>
    );
  }

  const isPaused  = campaign.status === "PAUSED";
  const isDraft   = campaign.status === "DRAFT";

  // Progress percentage
  const progress = stats.total > 0
    ? Math.round(((stats.completed + stats.failed + stats.skipped) / stats.total) * 100)
    : 0;

  return (
    <div className="space-y-6 p-4">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Button variant="ghost" size="sm" className="-ml-2" onClick={() => navigate("/campaigns")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Bot className="h-5 w-5 text-violet-500" />
            <h1 className="text-2xl font-bold">{campaign.name}</h1>
            <Badge variant={isActive ? "default" : "secondary"}>
              {campaign.status}
            </Badge>
          </div>
          {campaign.aiAgent?.name && (
            <p className="text-sm text-muted-foreground ml-8">Agent: {campaign.aiAgent.name}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {(isDraft || isPaused) && (
            <Button onClick={handleStart} disabled={isStarting} size="sm" className="gap-1.5">
              {isStarting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
              {isDraft ? "Start" : "Resume"}
            </Button>
          )}
          {isActive && (
            <Button onClick={handlePause} disabled={isPausing} size="sm" variant="outline" className="gap-1.5">
              {isPausing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pause className="h-3.5 w-3.5" />}
              Pause
            </Button>
          )}
          {(isActive || isPaused) && (
            <Button onClick={handleStop} disabled={isStopping} size="sm" variant="destructive" className="gap-1.5">
              {isStopping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Square className="h-3.5 w-3.5" />}
              Stop
            </Button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {stats.total > 0 && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{progress}% complete</span>
            <span>{stats.completed + stats.failed + stats.skipped} / {stats.total} contacts processed</span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Total" value={stats.total} />
        <StatCard label="Queued" value={stats.queued} className="text-muted-foreground" />
        <StatCard label="Active" value={stats.active} className={stats.active > 0 ? "text-blue-600" : ""} />
        <StatCard label="Completed" value={stats.completed} className="text-green-600" />
        <StatCard label="Failed / No answer" value={stats.failed} className={stats.failed > 0 ? "text-red-500" : ""} />
        <StatCard label="Skipped" value={stats.skipped} className="text-muted-foreground" />
      </div>

      {/* Active calls */}
      {activeContacts.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
              </span>
              Active Calls ({activeContacts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {activeContacts.map((c) => {
                const live = socketEvents[c.id];
                return (
                  <div key={c.id} className="rounded-xl border bg-muted/20 p-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-medium text-sm">{contactName(c)}</div>
                        {c.contact && <div className="text-xs text-muted-foreground">{c.phoneNumber}</div>}
                      </div>
                      <StatusBadge status={c.status} />
                    </div>
                    {live?.sentiment && <SentimentBadge sentiment={live.sentiment} />}
                    {live?.outcome && (
                      <div className="text-xs text-muted-foreground">{live.outcome.replace(/_/g, " ")}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Completed calls table */}
      {completedContacts.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">
              Processed Contacts ({completedContacts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sentiment</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Retries</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {completedContacts.map((c) => {
                  const live = socketEvents[c.id];
                  return (
                    <TableRow
                      key={c.id}
                      className={c.aiAgentCallId ? "cursor-pointer hover:bg-muted/50" : ""}
                      onClick={() => {
                        if (c.aiAgentCallId) {
                          // Navigate to the AI agent calls page — would need agentId which we don't have here.
                          // For now just do nothing; this is a future UX improvement.
                        }
                      }}
                    >
                      <TableCell>
                        <div className="font-medium text-sm">{contactName(c)}</div>
                        {c.contact && <div className="text-xs text-muted-foreground">{c.phoneNumber}</div>}
                      </TableCell>
                      <TableCell><StatusBadge status={c.status} /></TableCell>
                      <TableCell>
                        {live?.sentiment
                          ? <SentimentBadge sentiment={live.sentiment} />
                          : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">
                          {live?.outcome ? live.outcome.replace(/_/g, " ") : "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        {(c.retryCount ?? 0) > 0
                          ? <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <RefreshCw className="h-3 w-3" />{c.retryCount}
                            </span>
                          : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!contactsLoading && mergedContacts.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Bot className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-sm font-medium mb-1">No contacts yet</p>
            <p className="text-xs text-muted-foreground">
              Add contacts to this campaign from the{" "}
              <button
                className="underline text-primary"
                onClick={() => navigate(`/campaigns/${id}/edit`)}
              >
                edit page
              </button>
              .
            </p>
          </CardContent>
        </Card>
      )}

      <TelemarketingComplianceDialog
        open={isComplianceOpen}
        campaignId={id ?? null}
        source="ai"
        onOpenChange={setIsComplianceOpen}
        onConfirm={handleStartConfirmed}
      />
    </div>
  );
}
